import React from 'react';
import { View, Linking, Platform } from 'react-native';
import Typography from '../Text/Text';
import Button from '../Button/Button';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { styles, desktopStyles } from './CallBanner.styles';
import { RootState } from 'src/store/store';
import { useSelector } from 'react-redux';
import { useDesktopLayout } from 'src/hooks/useDesktopLayout';

interface CallBannerProps {
  /**
   * The phone number (digits only) to dial when "Call us" is pressed.
   * e.g. "18001234567"
   */
  phoneNumber?: string;
  bannerText?: string;
}

/**
 * A small banner that appears under each tab once the company is created.
 * Shows a prompt ("Would rather talk to a pro?") and a "Call us" button.
 * Pressing the button will open the native phone dialer (or tel: link on web).
 */
const CallBanner: React.FC<CallBannerProps> = ({
  phoneNumber = '+18643324036',
  bannerText = 'Have a Question? Talk to One of Our Agents Now!',
}) => {
  const { isDesktop } = useDesktopLayout();
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const handleCallPress = () => {
    trackGTMEvent(
      'call-icon-on-the-call-banner-pressed',
      {
        buttonTitle: 'Call us',
      },
      accountType,
    );
    // For both React Native (iOS/Android) and React Native Web, "tel:" URLs will work.
    // On web, this will open the default dialer application (e.g. Skype, or prompt).
    const telUrl = `tel:${phoneNumber}`;

    Linking.canOpenURL(telUrl)
      .then((supported) => {
        if (supported) {
          return Linking.openURL(telUrl);
        } else {
          console.warn('Cannot open dialer on this device/browser: ', telUrl);
        }
      })
      .catch((err) => {
        console.error('Error trying to open dialer:', err);
      });
  };

  return (
    <View style={[styles.container, isDesktop && desktopStyles.container]}>
      <Typography variant="body2" style={styles.text} numberOfLines={2} ellipsizeMode="tail">
        {bannerText}
      </Typography>
      {Platform.OS === 'web' ? (
        <a href={`tel:${phoneNumber}`} data-testid="call-link" style={{ textDecoration: 'none' }}>
          <Button
            type="primary"
            size="medium"
            title="Call us"
            onPress={handleCallPress}
            style={styles.button}
          />
        </a>
      ) : (
        <Button
          type="primary"
          size="medium"
          title="Call us"
          onPress={handleCallPress}
          style={styles.button}
        />
      )}
    </View>
  );
};

export default CallBanner;
